import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import debounce from "lodash-es/debounce";
import log from "logbeacon";
import { ViewState, CategoryType } from "./constants";
import { useSearchGroup } from "@/src/hooks/useSearchGroup";
import { OpenDataTypeEnum } from "@/src/features/api/openData";

export const useSearchLogic = () => {
  const [keywords, setKeywords] = useState("");
  const [debouncedKeywords, setDebouncedKeywords] = useState<string>("");
  const [viewState, setViewState] = useState<ViewState>(ViewState.RECOMMENDED);
  const [selectedCategory, setSelectedCategory] = useState<
    CategoryType | undefined
  >(undefined);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // 创建防抖函数 - 300ms延迟
  const debouncedSetKeywords = useMemo(
    () =>
      debounce((keywords: string) => {
        setDebouncedKeywords(keywords);
      }, 300),
    [],
  );

  // 监听搜索关键词变化，触发防抖
  useEffect(() => {
    debouncedSetKeywords(keywords);
    return () => {
      debouncedSetKeywords.cancel();
    };
  }, [keywords, debouncedSetKeywords]);

  // 滚动到顶部的功能
  // 使用自定义动画实现更流畅的滚动体验
  const scrollToTop = useCallback(() => {
    log.debug("[文案]contentScrollRef:", contentScrollRef.current);

    if (contentScrollRef.current) {
      const element = contentScrollRef.current;
      const startPosition = element.scrollTop;
      const targetPosition = 0;
      const distance = startPosition - targetPosition;
      const duration = 200; // 200ms 更快的滚动动画
      let startTime: number | null = null;

      log.debug("[contentScrollRef] 滚动前状态:", {
        scrollTop: startPosition,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
        canScroll: element.scrollHeight > element.clientHeight,
      });

      // 如果已经在顶部，不需要滚动
      if (startPosition === 0) {
        log.debug("[contentScrollRef] ✓ 已在顶部，无需滚动");
        return;
      }

      const animation = (currentTime: number) => {
        if (startTime === null) {
          startTime = currentTime;
        }
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        // 使用 easeOut 缓动函数，让滚动更流畅
        const easeOut = 1 - Math.pow(1 - progress, 3);
        element.scrollTop = startPosition - distance * easeOut;

        log.debug("[contentScrollRef] 滚动进度:", {
          progress: progress.toFixed(2),
          currentScrollTop: element.scrollTop.toFixed(2),
        });

        if (progress < 1) {
          requestAnimationFrame(animation);
        } else {
          log.debug("[contentScrollRef] ✓ 滚动完成");
        }
      };
      requestAnimationFrame(animation);
    } else {
      log.error("[contentScrollRef] ❌ 滚动容器引用为空！");
    }
  }, []);

  // 使用 useSearchGroup 获取搜索结果
  const { data: searchResults, isLoading: searchLoading } = useSearchGroup({
    keywords: debouncedKeywords,
    type: "portfolio",
  });

  // 处理搜索结果
  const { cryptoSearchData, stockSearchData } = useMemo(() => {
    let crypto: any[] = [];
    let stocks: any[] = [];

    if (searchResults && Array.isArray(searchResults)) {
      searchResults.forEach((group: any) => {
        if (group.type === OpenDataTypeEnum.TOKEN) {
          crypto = group.rows || [];
        } else if (group.type === OpenDataTypeEnum.STOCK) {
          stocks = group.rows || [];
        }
      });
    }

    return {
      cryptoSearchData: crypto,
      stockSearchData: stocks,
    };
  }, [searchResults]);

  // 处理搜索输入
  const handleSearchInput = useCallback((value: string) => {
    setKeywords(value);
    const newViewState = value.trim()
      ? ViewState.SEARCH_OVERVIEW
      : ViewState.RECOMMENDED;
    setViewState(newViewState);
    setSelectedCategory(undefined);
  }, []);

  // 清空搜索
  const handleClearSearch = useCallback(() => {
    setKeywords("");
    setViewState(ViewState.RECOMMENDED);
    setSelectedCategory(undefined);
  }, []);

  // View All 处理
  const handleViewAll = useCallback(
    (category: CategoryType) => {
      setSelectedCategory(category);
      setViewState(() => ViewState.CATEGORY_DETAIL);
      // 延迟 200ms 确保 React 完全更新并且 DOM 渲染完成
      setTimeout(() => {
        scrollToTop();
      }, 50);
    },
    [viewState, scrollToTop],
  );

  // 返回按钮处理
  const handleBack = useCallback(() => {
    setViewState(ViewState.SEARCH_OVERVIEW);
    setSelectedCategory(undefined);
    // 返回到搜索概览时，滚动到顶部
    setTimeout(() => {
      scrollToTop();
    }, 50); // 延迟 200ms 确保 DOM 完全更新
  }, [viewState, scrollToTop]);

  return {
    keywords,
    debouncedKeywords,
    viewState,
    selectedCategory,
    contentScrollRef,
    cryptoSearchData,
    stockSearchData,
    searchLoading,
    scrollToTop,
    handleSearchInput,
    handleClearSearch,
    handleViewAll,
    handleBack,
    setKeywords,
    setViewState,
    setSelectedCategory,
  };
};
